/**
 * Client mirror of control-api session_list_rbac role-scope filters.
 * Used while Administrator impersonates another role (JWT still Admin).
 */

import { allowlistPermitsAgent } from './agent-ids';

export const ADMINISTRATOR_ROLE_ID = 'role_administrator';

const AUTOTEST_EMAILS = new Set(['checkup-autotest@igentify.in']);

function normalizeEmail(value: unknown): string {
  return String(value || '').trim().toLowerCase();
}

export function isAutotestSession(it: {
  source?: string | null;
  ownerEmail?: string | null;
  userEmail?: string | null;
}): boolean {
  if (String(it.source || '').toLowerCase() === 'autotest') return true;
  const email = normalizeEmail(it.ownerEmail || it.userEmail);
  return Boolean(email && AUTOTEST_EMAILS.has(email));
}

export function isAdministratorOwnedSession(it: {
  ownerRoleId?: string | null;
}): boolean {
  return String(it.ownerRoleId || '') === ADMINISTRATOR_ROLE_ID;
}

function peerRestrictRoleIdsHas(
  ids: ReadonlySet<string> | string[],
  roleId: string,
): boolean {
  if (ids instanceof Set) return ids.has(roleId);
  if (Array.isArray(ids)) return ids.includes(roleId);
  return false;
}

export function peerRestrictRoleIdsFromRoles(
  roles: Array<{ roleId?: string; permissions?: string[] }>,
): Set<string> {
  const ids = new Set<string>();
  for (const role of roles) {
    const roleId = String(role.roleId || '').trim();
    if (!roleId) continue;
    if ((role.permissions || []).includes('sessions.restrict_peer_visibility')) {
      ids.add(roleId);
    }
  }
  return ids;
}

export function normalizeAllowedAgentIds(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const raw of value) {
    const id = String(raw || '').trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

/**
 * True when this session must be hidden for the effective (impersonated) viewer.
 * Pass `ignoreOwnerException: true` during Administrator role preview so the
 * Administrator's own sessions are not kept visible via the ownership exception.
 */
export function isHiddenByRoleScope(
  it: {
    agentId?: string | null;
    source?: string | null;
    ownerRoleId?: string | null;
    ownerEmail?: string | null;
    userEmail?: string | null;
    ownerUserId?: string | null;
  },
  viewer: {
    permissions?: string[];
    allowedAgentIds?: string[];
    userId?: string;
    email?: string;
    roleId?: string;
  } | null,
  opts?: {
    ignoreOwnerException?: boolean;
    /** Role ids with sessions.restrict_peer_visibility (impersonation preview). */
    peerRestrictRoleIds?: ReadonlySet<string> | string[];
  },
): boolean {
  if (!it || !viewer) return false;
  const perms = new Set(viewer.permissions || []);
  let own = false;
  if (!opts?.ignoreOwnerException) {
    const ownUserId = String(viewer.userId || '').trim();
    const ownEmail = normalizeEmail(viewer.email);
    const ownerUserId = String(it.ownerUserId || '').trim();
    const ownerEmail = normalizeEmail(it.ownerEmail || it.userEmail);
    own = Boolean(
      (ownerUserId && ownUserId && ownerUserId === ownUserId)
      || (ownerEmail && ownEmail && ownerEmail === ownEmail),
    );
  }

  if (isAutotestSession(it) && !perms.has('sessions.view_autotest') && !own) {
    return true;
  }

  if (isAdministratorOwnedSession(it) && !perms.has('sessions.view_admin_owned') && !own) {
    return true;
  }

  const ownerRoleId = String(it.ownerRoleId || '').trim();
  const peerIds = opts?.peerRestrictRoleIds;
  const peerRestricted = Boolean(
    ownerRoleId && peerIds && peerRestrictRoleIdsHas(peerIds, ownerRoleId),
  );
  if (peerRestricted && !own) {
    const viewerRoleId = String(viewer.roleId || '').trim();
    if (perms.has('sessions.view_peer_owned')) {
      // cross-role override
    } else if (viewerRoleId === ADMINISTRATOR_ROLE_ID) {
      // administrator sees all
    } else if (viewerRoleId && viewerRoleId === ownerRoleId) {
      // same role — role isolation, not user isolation
    } else {
      return true;
    }
  }

  const allowed = normalizeAllowedAgentIds(viewer.allowedAgentIds);
  const agentId = String(it.agentId || '').trim();
  if (agentId && !allowlistPermitsAgent(allowed, agentId)) {
    return true;
  }

  return false;
}

/** Filter session rows while Administrator is impersonating another role. */
export function filterSessionsForRolePreview<T extends {
  agentId?: string | null;
  source?: string | null;
  ownerRoleId?: string | null;
  ownerEmail?: string | null;
  userEmail?: string | null;
  ownerUserId?: string | null;
}>(
  sessions: T[],
  viewer: {
    permissions?: string[];
    allowedAgentIds?: string[];
    roleId?: string;
  } | null,
  opts?: { peerRestrictRoleIds?: ReadonlySet<string> | string[] },
): T[] {
  if (!viewer) return sessions;
  return sessions.filter(
    (s) => !isHiddenByRoleScope(s, viewer, {
      ignoreOwnerException: true,
      peerRestrictRoleIds: opts?.peerRestrictRoleIds,
    }),
  );
}

export function filterAgentsByAllowlist<T extends { agentId: string }>(
  agents: T[],
  allowedAgentIds: string[] | undefined,
): T[] {
  const allowed = normalizeAllowedAgentIds(allowedAgentIds);
  return agents.filter((a) => allowlistPermitsAgent(allowed, String(a.agentId)));
}
